import "../AdminGroupDetails/style.css";

import { CirclePlus } from "lucide-react";
import { observer } from "mobx-react-lite";
import type { FC } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  useDeleteBranchMutation,
  useUpdateBranchMutation,
} from "@/api/admin/branches";
import { type BranchesInBrand, type IBrandGetById } from "@/api/admin/brands";
import { AdminSections } from "@/api/admin/teamRoles";
import { QUERY_KEYS } from "@/api/constants";
import { queryClient } from "@/api/queryClient";
import { CustomButton, DetailsBlock } from "@/components/atoms";
import {
  BrandFieldEdit,
  toastError,
  toastSuccess,
} from "@/components/molecules";
import { PATHS } from "@/config/path";
import { cn } from "@/lib/utils";
import {
  BRANCH_WIZARD_RETURN_TO_KEY,
  setWizardReturnTo,
} from "@/modules/wizardReturnTo";
import { usePermission } from "@/services/casl/usePermission";
import { commonIconProps20 } from "@/utils/constants";

import Contacts from "./components/Contacts";
import Details from "./components/Details";

interface AdminBrandDetailsProps {
  data: IBrandGetById;
  hasUpdatePermission?: boolean;
}

const AdminBrandDetails: FC<AdminBrandDetailsProps> = observer(
  ({ data, hasUpdatePermission }) => {
    const navigate = useNavigate();
    const { id = "" } = useParams();
    const branches = data?.branches || [];

    const { mutate: deleteBranch, isPending: isDeletePending } =
      useDeleteBranchMutation();
    const { mutate: updateBranch, isPending: isUpdatePending } =
      useUpdateBranchMutation();

    const handleEditBranch = (branchId: string) => {
      setWizardReturnTo(
        BRANCH_WIZARD_RETURN_TO_KEY,
        PATHS.ADMIN.ACCOUNT_BRAND_DETAILS(id)
      );
      navigate(PATHS.ADMIN.BRANCH_WIZARD_DETAILS(branchId, id));
    };

    const handleDeleteBranch = (branch: BranchesInBrand) => {
      if (branch.active) {
        deleteBranch(branch.branchId, {
          onSuccess: () => {
            toastSuccess("Branch was deactivated successfully!");
            queryClient.invalidateQueries({
              queryKey: [QUERY_KEYS.brands, id],
            });
          },
          onError: err => {
            toastError(
              err?.message || "Failed to deactivate branch.",
              () => handleDeleteBranch(branch),
              "Error"
            );
          },
        });
      } else {
        updateBranch(
          {
            id: branch.branchId,
            data: {
              ...branch,
              active: true,
            },
          },
          {
            onSuccess: () => {
              toastSuccess("Branch was activated successfully!");
              queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.brands, id],
              });
            },
            onError: err => {
              toastError(
                err?.message || "Failed to activate branch.",
                () => handleDeleteBranch(branch),
                "Error"
              );
            },
          }
        );
      }
    };

    const hasBranchPermission = usePermission(AdminSections.BRANCH);

    return (
      <div className="pt-6 flex gap-6 w-full flex-col md:flex-row">
        <div className="w-full md:w-[62%] flex flex-col gap-[17px]">
          <Details data={data} hasUpdatePermission={hasUpdatePermission} />
          <Contacts data={data} hasUpdatePermission={hasUpdatePermission} />

          <DetailsBlock
            title="Branches under brand"
            classes="sticky top-0 pt-[20px] bg-white z-10 gap-0 max-h-[530px]"
          >
            <>
              {branches.length > 0 && (
                <ul
                  className="border border-gray-200 rounded-t-[6px] text-sm bg-white mt-[24px] 
                 overflow-y-scroll flex-1"
                >
                  {branches?.map(branch => (
                    <BrandFieldEdit
                      key={branch.branchId}
                      name={branch.branchName}
                      isTrash
                      classes="py-[7px] pr-0"
                      editClik={() => handleEditBranch(branch.branchId)}
                      removeClick={() => {
                        handleDeleteBranch(branch);
                      }}
                      state="branch"
                      isPending={isDeletePending || isUpdatePending}
                      isActive={branch.active}
                      linkToEntity={
                        hasBranchPermission
                          ? PATHS.ADMIN.ACCOUNT_BRANCH(branch.branchId)
                          : ""
                      }
                      disabled={!hasUpdatePermission || !data.active}
                      hasUpdatePermission={hasUpdatePermission}
                    />
                  ))}
                </ul>
              )}
              {hasUpdatePermission && (
                <div
                  className={cn(
                    "relative border border-gray-200 pr-4 pl-3 py-[6px] bg-gray-50",
                    branches.length > 0
                      ? "border-t-0 rounded-b-[8px]"
                      : "border rounded-[8px] mt-6"
                  )}
                >
                  <CustomButton
                    variant="ghost"
                    type="button"
                    onClick={() => {
                      setWizardReturnTo(
                        BRANCH_WIZARD_RETURN_TO_KEY,
                        PATHS.ADMIN.ACCOUNT_BRAND_DETAILS(id)
                      );
                      navigate(
                        PATHS.ADMIN.BRANCH_WIZARD_DETAILS(undefined, id)
                      );
                    }}
                    data-testid="admin-brand-add-branch-button"
                  >
                    <CirclePlus {...commonIconProps20} />
                    Add new branch
                  </CustomButton>
                </div>
              )}
            </>
          </DetailsBlock>
        </div>
        {/* TODO:hidden until full integration  */}
        {/* <DetailsBlock
          classes={cn(
            "w-full md:w-[37%] p-[32px] pb-0 h-fit gap-0!",
            isDevelope && "outline-[#DC2626] outline-2"
          )}
        >
          <DetailsOverviewToday
            title="Branches overview"
            positions={positions}
            classes="p-0"
          />
        </DetailsBlock> */}
      </div>
    );
  }
);

export default AdminBrandDetails;
